/**
 * SubscriptionJurosSection - Seção colapsável de Juros e Multa por atraso
 *
 * Permite configurar:
 *  - Multa (única): por percentual (%) OU valor fixo (R$)
 *  - Juros (mensal): por percentual (% a.m.) OU valor fixo (R$/mês)
 *
 * Persistência: campo `juros` jsonb da tabela `financeiro_assinaturas`.
 * Estrutura:
 *   { enabled, multa_tipo, multa_valor, juros_tipo, juros_valor }
 *
 * Suporta modo readOnly (modal de detalhes) e edição (criação/edição).
 *
 * @version 1.0.0
 * RELEASE: 2026-06-10 v1.0.0 - Criação inicial.
 */
import { useState } from 'react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ChevronDown, Percent, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

export type JurosTipo = 'percent' | 'fixed';

export interface JurosValue {
  enabled: boolean;
  multa_tipo: JurosTipo;
  multa_valor: number;
  juros_tipo: JurosTipo;
  juros_valor: number;
}

export const JUROS_DEFAULT: JurosValue = {
  enabled: false,
  multa_tipo: 'percent',
  multa_valor: 0,
  juros_tipo: 'percent',
  juros_valor: 0,
};

interface Props {
  value: JurosValue;
  onChange?: (value: JurosValue) => void;
  readOnly?: boolean;
  /** Estado inicial expandido (default: true em edição, false em readOnly) */
  defaultOpen?: boolean;
}

/** Formata BRL para exibição readOnly. */
function formatBRL(v: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
}

/** Sanitiza input numérico (>=0). Retorna 0 se inválido. */
function parseNumber(raw: string): number {
  const n = parseFloat(raw.replace(',', '.'));
  if (Number.isNaN(n) || n < 0) return 0;
  return n;
}

export function SubscriptionJurosSection({
  value,
  onChange,
  readOnly = false,
  defaultOpen,
}: Props) {
  const [open, setOpen] = useState(defaultOpen ?? !readOnly);
  const v = value || JUROS_DEFAULT;

  const update = (patch: Partial<JurosValue>) => {
    if (readOnly || !onChange) return;
    onChange({ ...v, ...patch });
  };

  // Validações inline (defesa em profundidade — backend revalida)
  const multaInvalid =
    v.multa_tipo === 'percent' && v.multa_valor > 100;
  const jurosInvalid =
    v.juros_tipo === 'percent' && v.juros_valor > 100;

  return (
    <section className="rounded-lg border border-border/50">
      <Collapsible open={open} onOpenChange={setOpen}>
        <CollapsibleTrigger className="w-full flex items-center gap-2 px-3 py-2.5 group focus:outline-none">
          <Percent className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Juros e Multa por atraso</h3>
          {v.enabled && (
            <span className="text-[10px] uppercase tracking-wide font-semibold text-emerald-600 dark:text-emerald-400 px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-950">
              ativo
            </span>
          )}
          <ChevronDown
            className={cn(
              'h-4 w-4 ml-auto text-muted-foreground transition-transform',
              open && 'rotate-180',
            )}
          />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-3 pb-3 pt-1 space-y-3">
            {/* Toggle ON/OFF */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1">
                <Label className="text-xs font-medium">Cobrar multa e juros em caso de atraso</Label>
                <p className="text-[11px] text-muted-foreground">
                  Aplicado nas próximas cobranças geradas. Ciclos já emitidos não são alterados.
                </p>
              </div>
              {readOnly ? (
                <span className="text-xs font-medium text-muted-foreground">
                  {v.enabled ? 'Sim' : 'Não'}
                </span>
              ) : (
                <Switch
                  checked={v.enabled}
                  onCheckedChange={(checked) => update({ enabled: checked })}
                />
              )}
            </div>

            {v.enabled && (
              <div className="space-y-3 pt-1 border-t border-border/40">
                {/* Multa */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Multa (cobrada uma única vez)</Label>
                  {readOnly ? (
                    <p className="text-sm font-medium">
                      {v.multa_valor > 0
                        ? v.multa_tipo === 'percent'
                          ? `${v.multa_valor}%`
                          : formatBRL(v.multa_valor)
                        : '—'}
                    </p>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Select
                        value={v.multa_tipo}
                        onValueChange={(t) => update({ multa_tipo: t as JurosTipo })}
                      >
                        <SelectTrigger className="w-28 h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="percent">%</SelectItem>
                          <SelectItem value="fixed">R$</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        min={0}
                        max={v.multa_tipo === 'percent' ? 100 : undefined}
                        step="0.01"
                        value={v.multa_valor || ''}
                        onChange={(e) => update({ multa_valor: parseNumber(e.target.value) })}
                        placeholder="0,00"
                        className="h-9"
                      />
                    </div>
                  )}
                  {multaInvalid && !readOnly && (
                    <p className="text-[11px] text-destructive flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Multa em % não pode passar de 100.
                    </p>
                  )}
                </div>

                {/* Juros — somente % a.m. (limitação do provider Asaas: não aceita juros fixos em R$) */}
                <div className="space-y-1.5">
                  <Label className="text-xs">Juros (por mês de atraso)</Label>
                  {readOnly ? (
                    <p className="text-sm font-medium">
                      {v.juros_valor > 0 ? `${v.juros_valor}% a.m.` : '—'}
                    </p>
                  ) : (
                    <div className="flex items-center gap-2">
                      {/* Tipo fixo em % — provider não suporta juros em valor fixo */}
                      <div className="w-28 h-9 flex items-center justify-center rounded-md border border-input bg-muted/40 text-xs font-medium text-muted-foreground">
                        % a.m.
                      </div>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step="0.01"
                        value={v.juros_valor || ''}
                        onChange={(e) => update({ juros_valor: parseNumber(e.target.value), juros_tipo: 'percent' })}
                        placeholder="0,00"
                        className="h-9"
                      />
                    </div>
                  )}
                  {jurosInvalid && !readOnly && (
                    <p className="text-[11px] text-destructive flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Juros em % não pode passar de 100.
                    </p>
                  )}
                  {!readOnly && (
                    <p className="text-[11px] text-muted-foreground">
                      O provedor de cobrança aceita apenas juros em percentual ao mês.
                    </p>
                  )}
                </div>

              </div>
            )}

            {!v.enabled && readOnly && (
              <p className="text-xs text-muted-foreground italic">Não configurado.</p>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </section>
  );
}

export default SubscriptionJurosSection;
